import { useCallback } from 'react';
import {
  api,
  MarketplaceArche,
  MarketplaceListingCard,
  MarketplaceSearchResult,
  ArcheType,
} from '../../lib/api';
import { buildSearchKey, usePagedSearch } from './usePagedSearch';

interface UseMarketplaceSearchOptions {
  query?: string;
  categoryId?: string;
  archeType?: ArcheType;
  tags?: string[];
  sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
  limit?: number;
}

interface UseMarketplaceSearchReturn {
  results: MarketplaceSearchResult | null;
  loading: boolean;
  error: string | null;
  arches: MarketplaceArche[];
  /** Matching listings inside arches (Wordtrace in Flappyland), first page only. */
  listings: MarketplaceListingCard[];
  total: number;
  hasMore: boolean;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  refresh: () => void;
}

export function useMarketplaceSearch(
  options: UseMarketplaceSearchOptions = {},
): UseMarketplaceSearchReturn {
  const { query, categoryId, archeType, tags, sortBy, limit = 20 } = options;

  const searchKey = buildSearchKey([query, categoryId, archeType, tags ?? [], sortBy]);

  // Only `searchKey` gates the request, so an unmemoised `tags` array from a
  // caller cannot re-fire it every render.
  const request = useCallback(
    (requestLimit: number, offset: number, signal: AbortSignal) =>
      api.searchMarketplace({
        q: query,
        categoryId,
        archeType,
        tags,
        sortBy,
        limit: requestLimit,
        offset,
        signal,
      }),
    // Intentionally keyed on searchKey alone: it already encodes every
    // parameter below, and depending on them directly would reintroduce the
    // identity trap this replaced.
    [searchKey],
  );

  const { results, loading, error, page, setPage, refresh } =
    usePagedSearch<MarketplaceSearchResult>({
      searchKey,
      limit,
      request,
      telemetryParams: { q: query, categoryId, archeType, tags, sortBy },
      telemetry: {
        traceName: 'marketplaceSearch',
        startMessage: 'Starting search',
        resultMessage: (result) => `Search returned ${result.arches.length} arches`,
        resultDetails: (result) => ({
          archeCount: result.arches.length,
          total: result.total,
          hasMore: result.hasMore,
          archeIds: result.arches.slice(0, 5).map((a) => a.archId),
        }),
        failureMessage: (message) => `Search failed: ${message}`,
      },
      fallbackErrorMessage: 'Failed to search',
    });

  const arches = results?.arches ?? [];
  const listings = results?.listings ?? [];
  const total = results?.total ?? 0;
  const hasMore = results?.hasMore ?? false;
  const totalPages = Math.ceil(total / limit);

  return {
    results,
    loading,
    error,
    arches,
    listings,
    total,
    hasMore,
    page,
    setPage,
    totalPages,
    refresh,
  };
}
